import { cloneDeep, differenceWith, find, isEqual, omit } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { REDUX_STORE } from '@funnels/constants';
import {
  Asset,
  CreateRelatedPartyAction,
  DeleteRelatedPartyAction,
  FetchRelatedPartyAction,
  ReduxStoreData,
  RelatedParty,
  ServerRelatedPartyCreate,
  ServerRelatedPartyUpdate,
  StoreApplicant,
  StoreEmployment,
  UpdateRelatedPartyAction,
  UpsertRelatedPartyAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { waitForAuth, waitForResourcesToBeCreated, waitForResourcesToBeFree } from '../_common';
import {
  createRelatedParty,
  createRelatedPartyFail,
  createRelatedPartySuccess,
  deleteRelatedPartyFail,
  deleteRelatedPartySuccess,
  fetchRelatedPartyFail,
  fetchRelatedPartySuccess,
  updateRelatedParty,
  updateRelatedPartyFail,
  updateRelatedPartySuccess,
  upsertRelatedPartyFail,
  upsertRelatedPartySuccess,
} from './actions';
import RelatedPartyAPI from './api';
import { fixRelatedPartyForeignIds, getRelatedPartyId } from './utils';

export function* createRelatedPartyWorker(action: CreateRelatedPartyAction): any {
  try {
    yield waitForAuth();
    const {
      applicationId,
      relatedParty: { clientId },
    } = action;

    const relatedParty = omit(action.relatedParty, [
      'clientId',
      'applicationId',
      'applicantClientId',
      'trustApplicantClientId',
      'companyApplicantClientId',
    ]) as ServerRelatedPartyCreate;

    const response = yield call(RelatedPartyAPI.create, applicationId, relatedParty);

    yield put(createRelatedPartySuccess(clientId, response.data.id));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      relatedParty: { clientId },
    } = action;
    yield put(createRelatedPartyFail(clientId, e.response?.data || e.message));
  }
}

export function* deleteRelatedPartyWorker(action: DeleteRelatedPartyAction) {
  try {
    yield waitForAuth();
    const { applicationId, clientId } = action;

    yield call(RelatedPartyAPI.del, applicationId, clientId);

    yield put(deleteRelatedPartySuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield put(deleteRelatedPartyFail(clientId, e.response?.data || e.message));
  }
}

export function* fetchRelatedPartyWorker(action: FetchRelatedPartyAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;

    let response;

    response = yield call(RelatedPartyAPI.fetchAll, applicationId);

    const relatedParty = response.data.map((relatedParty: any) =>
      omit(relatedParty, ['meta', 'updatedAt', 'isDeidentified', 'isDeidentified', 'isDeleted', 'createdAt'])
    );
    yield put(fetchRelatedPartySuccess(relatedParty));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(fetchRelatedPartyFail(e.response?.data || e.message));
  }
}

export function* updateRelatedPartyWorker(action: UpdateRelatedPartyAction): any {
  try {
    yield waitForAuth();
    const { applicationId } = action;
    const relatedPartyId = yield getRelatedPartyId(action.relatedParty.clientId, action.relatedParty.id);

    const relatedParty = omit(action.relatedParty, [
      'id',
      'clientId',
      'createdAt',
      'isDeidentified',
      'isDeleted',
      'applicationId',
      'applicantClientId',
      'trustApplicantClientId',
      'companyApplicantClientId',
    ]) as ServerRelatedPartyUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!relatedPartyId) {
      yield all([put(createRelatedParty(applicationId, action.relatedParty))]);
    } else {
      yield call(RelatedPartyAPI.update, applicationId, relatedPartyId, relatedParty);

      yield put(updateRelatedPartySuccess());
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      relatedParty: { clientId },
    } = action;
    yield put(updateRelatedPartyFail(clientId, e.response?.data || e.message));
  }
}

export function* upsertRelatedPartyWorker(action: UpsertRelatedPartyAction) {
  try {
    yield waitForAuth();

    const { applicationId, relatedParties } = action;

    let reduxStoreApplicants: StoreApplicant[] = yield select((state: ReduxStoreData) => state.applicants.data);
    const reduxStoreRelatedParties: RelatedParty[] = yield select((state: ReduxStoreData) => state.relatedParties.data);
    const reduxStoreEmployments: StoreEmployment[] = yield select((state: ReduxStoreData) => state.employments.data);
    const reduxStoreAssets: Asset[] = yield select((state: ReduxStoreData) => state.assets.data);

    yield waitForResourcesToBeCreated({
      applicants: reduxStoreApplicants.map((a: StoreApplicant) => a.clientId),
    });
    yield waitForResourcesToBeFree(['applicants', 'relatedParties']);
    reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);

    // Filter out Id in the comparator function because its possible for that to be added by the store
    const changedRelatedParties = differenceWith(
      relatedParties,
      reduxStoreRelatedParties,
      ({ id: id1, clientId: clientId1, ...rest1 }, { id: id2, clientId: clientId2, ...rest2 }) => isEqual(rest1, rest2)
    );

    yield all(
      cloneDeep(changedRelatedParties).map((clientRelatedParty) => {
        // this is for fixing RelatedParty's applicantId, employmentId, assetId for clone case
        fixRelatedPartyForeignIds(clientRelatedParty, reduxStoreApplicants, reduxStoreEmployments, reduxStoreAssets);
        // We rely on grabbing the id from the store so that
        // we DON'T rely on the store syncing to the form after initialisation
        const reduxStoreRelatedParty = find(reduxStoreRelatedParties, {
          clientId: clientRelatedParty.clientId,
        }) as RelatedParty;

        if (!reduxStoreRelatedParty) {
          // No relatedParty
          return put(createRelatedParty(applicationId, clientRelatedParty));
        } else if (!reduxStoreRelatedParty.id) {
          // relatedParty but no id, therefore create has been fired but success hasn't returned
          return put(updateRelatedParty(applicationId, clientRelatedParty));
        }

        // RelatedParty is in the store and has an id
        return put(updateRelatedParty(applicationId, { id: reduxStoreRelatedParty.id, ...clientRelatedParty }));
      })
    );
    yield put(upsertRelatedPartySuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(upsertRelatedPartyFail(e.response?.data || e.message));
  }
}

export function* relatedPartyWatcher() {
  yield takeEvery(REDUX_STORE.RELATED_PARTY.ACTION_TYPES.CREATE_RELATED_PARTY, createRelatedPartyWorker);
  yield takeEvery(REDUX_STORE.RELATED_PARTY.ACTION_TYPES.DELETE_RELATED_PARTY, deleteRelatedPartyWorker);
  yield takeEvery(REDUX_STORE.RELATED_PARTY.ACTION_TYPES.FETCH_RELATED_PARTY, fetchRelatedPartyWorker);
  yield takeEvery(REDUX_STORE.RELATED_PARTY.ACTION_TYPES.UPDATE_RELATED_PARTY, updateRelatedPartyWorker);
  yield takeEvery(REDUX_STORE.RELATED_PARTY.ACTION_TYPES.UPSERT_RELATED_PARTY, upsertRelatedPartyWorker);
}

export default relatedPartyWatcher;
